import { CheckmateCaseHistoryIcon, CheckmateEditIcon } from '../../shared/CheckmateIcons';
import { EmptyGuid, UserRightsEnum } from '../../utilities/Constants';

import Authorization from '../../stores/Authorization';
import CaseHelper from '../../utilities/CaseHelper';
import { CaseName } from './CaseName';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { Link } from 'react-router-dom';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import ValidateUtils from '../../shared/validations';
import { useCaseFileContext } from '../../contexts/useCaseFileContext';
import { useZoneContext } from '../../contexts/useZoneContext';

export interface ICaseFileHeaderProps {
    enableCaseFileEdit: boolean;
    enablePlanningExecution: boolean;
    hasUnsavedChanges: boolean;
    isSystemAdmin: boolean;
    onCancelClick: () => void;
    onClickAddNote: () => void;
    onClickDeleteCase: () => void;
    onClickSetCasePriority: () => void;
    onDefenseCounselClick: () => void;
    onHistoryClick: () => void;
    onSaveClick: () => void;
    onCaseResolutionClick: () => void;
    user: IUserModel;
    validation: IValidation;
}

export function CaseFileHeader({
    enableCaseFileEdit,
    enablePlanningExecution,
    hasUnsavedChanges,
    isSystemAdmin,
    onCancelClick,
    onClickAddNote,
    onClickDeleteCase,
    onClickSetCasePriority,
    onDefenseCounselClick,
    onSaveClick,
    onHistoryClick,
    onCaseResolutionClick,
    user,
    validation,
}: ICaseFileHeaderProps) {
    const {
        currentCase,
        isReadOnlyMode,
        isCaseApprovedInSameCaseGroup,
        isCasePendingInSameCaseGroup,
        setIsReadOnlyMode,
    } = useCaseFileContext();
    const zoneContext = useZoneContext();

    const isLocalCounselAssigned =
        (currentCase.defenseCounsels?.filter(
            (x) => x.guid && x.guid != EmptyGuid && x.priority && x.priority == 1
        ).length ?? 0) > 0;

    const showEditSaveButtons = Authorization.userHasRight(
        UserRightsEnum.EditCaseFile,
        user,
        zoneContext.zoneGuid
    );

    const showPriorityToggleButton =
        isReadOnlyMode &&
        enableCaseFileEdit &&
        !isSystemAdmin &&
        currentCase &&
        Authorization.userHasRight(UserRightsEnum.EditCaseFile, user, zoneContext.zoneGuid);

    const showDeleteCaseButton =
        isReadOnlyMode &&
        enableCaseFileEdit &&
        !isSystemAdmin &&
        currentCase &&
        Authorization.userHasRight(UserRightsEnum.DeleteCase, user, zoneContext.zoneGuid) &&
        !isCaseApprovedInSameCaseGroup &&
        !isCasePendingInSameCaseGroup;

    const showAddCaseButton =
        currentCase.guid &&
        (Authorization.userHasRight(UserRightsEnum.EditCaseFile, user, zoneContext.zoneGuid) ||
            Authorization.userHasRight(UserRightsEnum.AddCaseNote, user, zoneContext.zoneGuid));

    const hasValidationItems = Object.keys(validation).length > 0;
    const catchAllValidationKeys = getCatchAllValidationItems(validation);

    return (
        <>
            <h1 style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                    <CaseName
                        caseTitle={CaseHelper.getCaseTitle(currentCase, false)}
                        zoneContext={zoneContext}
                        caseGuid={currentCase.guid}
                    />
                </div>
                <span className="float-end print-hide-display">
                    <span>
                        {/* Only checking for specific Planning & Execution Rights. Checking whether the logged in user has access to the case File is done prior to here */}
                        {enablePlanningExecution && isLocalCounselAssigned ? (
                            Authorization.userHasRight(
                                UserRightsEnum.FullPlanningAndExecution,
                                user,
                                zoneContext.zoneGuid
                            ) ||
                            (currentCase.casePlan != undefined &&
                                (Authorization.userHasRight(
                                    UserRightsEnum.ReadOnlyPlanningAndExecution,
                                    user,
                                    zoneContext.zoneGuid
                                ) ||
                                    Authorization.userHasRight(
                                        UserRightsEnum.FirmLevelPlanningAndExecution,
                                        user,
                                        zoneContext.zoneGuid
                                    ))) ? (
                                <span className="btn btn-blue float-end ">
                                    {' '}
                                    <Link
                                        to={zoneContext.buildZoneUrl(
                                            LocalRoutes.CasePlan.replace(':guid', currentCase.guid!)
                                        )}
                                        style={{ color: 'white' }}
                                    >
                                        Case plan
                                    </Link>
                                </span>
                            ) : null
                        ) : null}
                        {showEditSaveButtons && (
                            <>
                                {!isReadOnlyMode ? (
                                    <>
                                        <button
                                            className="btn btn-default float-end text-gray"
                                            onClick={onCancelClick}
                                        >
                                            Cancel
                                        </button>
                                        {hasUnsavedChanges && (
                                            <button
                                                className="btn btn-orange float-end text-gray"
                                                onClick={onSaveClick}
                                            >
                                                Save
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <button
                                        className="btn btn-no-bg float-end text-gray"
                                        onClick={() => setIsReadOnlyMode(false)}
                                    >
                                        <CheckmateEditIcon />
                                        &nbsp;Edit
                                    </button>
                                )}
                            </>
                        )}
                        {showAddCaseButton && (
                            <button
                                className="btn btn-no-bg float-end text-gray"
                                onClick={onClickAddNote}
                            >
                                <i className="fal fa-memo" />
                                &nbsp;Add Case Note
                            </button>
                        )}
                        {showPriorityToggleButton && (
                            <button
                                className="btn btn-no-bg float-end text-gray"
                                onClick={onClickSetCasePriority}
                            >
                                <i className="fal fa-exclamation-circle" />
                                &nbsp;
                                {currentCase.priority ? 'Remove Priority' : 'Mark as Priority'}
                            </button>
                        )}
                        {showDeleteCaseButton && (
                            <button
                                className="btn btn-no-bg float-end text-gray"
                                onClick={onClickDeleteCase}
                            >
                                <i className="fal fa-trash-alt" />
                                &nbsp;Delete Case
                            </button>
                        )}
                        {isLocalCounselAssigned && (
                            <button
                                className="btn btn-no-bg float-end text-gray"
                                onClick={onDefenseCounselClick}
                            >
                                <i className="fal fa-building" />
                                &nbsp;Defense Counsel
                            </button>
                        )}
                        <button
                            className="btn btn-no-bg float-end text-gray"
                            onClick={onHistoryClick}
                        >
                            <CheckmateCaseHistoryIcon />
                            &nbsp;History
                        </button>
                    </span>
                </span>
            </h1>
            <hr className="no-margin" />
            {hasValidationItems && (
                <div style={{ marginTop: '5px' }}>
                    <ul>
                        {validation.casestatus && (
                            <li>
                                <span className="text-danger">{validation.casestatus}</span>
                            </li>
                        )}
                        {validation.casenumber && (
                            <li>
                                <span className="text-danger">{validation.casenumber}</span>
                            </li>
                        )}
                        {validation.defenseCounsel && (
                            <li>
                                <span className="text-danger">{validation.defenseCounsel}</span>
                            </li>
                        )}
                        {validation.model &&
                            ValidateUtils.parseModelErrors(validation.model).map(
                                (value: string) => {
                                    return (
                                        <li key={value}>
                                            <span className="text-danger">{value}</span>
                                        </li>
                                    );
                                }
                            )}
                        {catchAllValidationKeys.map((key) => {
                            const validationItem = validation[key];
                            return validationItem.map((vi) => {
                                return (
                                    <li key={vi}>
                                        <span className="text-danger">{vi}</span>
                                    </li>
                                );
                            });
                        })}
                    </ul>
                </div>
            )}
        </>
    );
}

const getCatchAllValidationItems = (validation: IValidation) => {
    const excludedKeys = ['casestatus', 'casenumber', 'defenseCounsel', 'model'];
    return Object.keys(validation).filter((key) => !excludedKeys.includes(key));
};
